import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    Edit2, Ban, RotateCcw, Trash2, MessageSquare,
    Mail, Phone, Building2, MapPin, Plane, Train,
    Plus, AlertTriangle,
Users, UserRound, Receipt, Bell, BellOff,
    CreditCard, ChevronRight, Send, Check, Clock, CheckCircle2,
} from 'lucide-react';

import { PaymentMethodsTab } from './PaymentMethodsTab.jsx';
import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input, Textarea } from '../../components/ui/Form.jsx';
import AddressInput from '../../components/AddressInput.jsx';
import { classifyAddressIcon } from '../../lib/addressCanonicalization.js';
import { customerDisplayName, customerContactPerson } from '../../lib/customer.js';
import { SalesExecCard } from './SalesExecCard.jsx';

// Minimal local cn helper — avoids a new import just for className concatenation.
function cn(...classes) {
    return classes.filter(Boolean).join(' ');
}

// ============================================================================
// CustomerDetailPage — mirrors VehicleDetailPage shape
// ============================================================================
export function CustomerDetailPage() {
    const { id } = useParams();
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAdmin, isOwner } = useCurrentUser();

    const [showAddAddressModal, setShowAddAddressModal] = useState(false);
    const [deleteAddressTarget, setDeleteAddressTarget] = useState(null);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [showEnableModal, setShowEnableModal] = useState(false);

  const customerQuery = useQuery({
        queryKey: ['customer', id],
        queryFn: () => api.get(`/customers/${id}`),
    });

    // Discount lookup — used by Preferences card. Visible to all dispatchers
    // (backend gates the WRITE endpoints with requireOwner; READ is open).
    // Endpoint returns null when no discount, so the row just doesn't render.
    const discountQuery = useQuery({
        queryKey: ['pricing', 'discounts', 'by-customer', id],
        queryFn: () => api.get(`/pricing/admin/discounts/by-customer/${id}`),
        enabled: Boolean(id),
    });

    if (customerQuery.isLoading) return <LoadingPage />;
    if (customerQuery.isError) return <ErrorPage message={customerQuery.error?.message} onRetry={customerQuery.refetch} />;

  const customer = customerQuery.data;
    const addresses = customer.addresses || [];
    const notes = customer.notes || [];
    const savedNotes = customer.saved_notes || [];
    const isActive = customer.status === 'active';

    const actions = isAdmin ? (
        <Button variant="secondary" onClick={() => navigate(`/customers/${id}/edit`)}>
            <Edit2 className="h-4 w-4" />
            <span className="hidden sm:inline">Edit</span>
        </Button>
    ) : null;

    return (
        <div>
<PageHeader
    title={customerDisplayName(customer)}
    subtitle={
        <span className="inline-flex items-center gap-2 flex-wrap">
            {customer.customer_type && (
                <span className="text-ink-muted text-sm">{customer.customer_type}</span>
            )}
            <StatusPill status={isActive ? 'success' : 'neutral'}>
                {customer.status}
            </StatusPill>
        </span>
    }
    backTo="/customers"
    backLabel="Customers"
    actions={actions}
/>

            {!isActive && (
                <div className="card mb-5 border-amber-200 bg-amber-50">
                    <div className="card-body flex items-start gap-3">
                        <AlertTriangle className="h-4 w-4 text-status-warning flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-status-warning">
                                This customer is inactive and hidden from the default list.
                            </div>
                            {customer.disabled_reason && (
                                <div className="text-xs text-status-warning/80 mt-1">
                                    {customer.disabled_reason}
                                </div>
                            )}
                            {customer.disabled_at && (
                                <div className="text-2xs text-status-warning/70 mt-1">
                                    Disabled {formatDate(customer.disabled_at)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left column */}
<div className="lg:col-span-1 space-y-5">
                    <ContactCard customer={customer} isOwner={isOwner} />
                                        <NotificationsCard customer={customer} onCustomerChanged={customerQuery.refetch} />
                    {isOwner && <SalesExecCard customerId={customer.id} />}
                    {(customer.payment_terms || customer.default_pickup_notes || discountQuery.data) && (
                        <PreferencesCard customer={customer} discount={discountQuery.data} />
                    )}

                    {isOwner && isActive && (
                        <div className="card">
                            <div className="card-body">
                                <Button
                                    variant="secondary"
                                    className="w-full text-status-danger hover:text-status-danger"
                                    onClick={() => setShowDisableModal(true)}
                                >
                                    <Ban className="h-4 w-4" />
                                    Disable customer
                                </Button>
                            </div>
                        </div>
                    )}
                    {isOwner && !isActive && (
                        <div className="card">
                            <div className="card-body">
                                <Button
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => setShowEnableModal(true)}
                                >
                                    <RotateCcw className="h-4 w-4" />
                                    Re-enable customer
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right column */}
                <div className="lg:col-span-2 space-y-5">
                    <AddressesCard
                        addresses={addresses}
                        isAdmin={isAdmin}
                        onAdd={() => setShowAddAddressModal(true)}
                        onDelete={(addr) => setDeleteAddressTarget(addr)}
                    />
                    <SavedNotesCard
                        customerId={id}
                        savedNotes={savedNotes}
                        isAdmin={isAdmin}
                        onChanged={customerQuery.refetch}
                    />
                    {/* Round 7d.1.5 — replaces the old BookingHistoryCard placeholder.
                        Tabs: Bookings (default), Bookers, Passengers, Payment methods. */}
                    <CustomerSubresources
                        customerId={id}
                        customer={customer}
                        isOwner={isOwner}
                        onCustomerChanged={customerQuery.refetch}
                    />
                    <NotesCard
                        customerId={id}
                        notes={notes}
                        isOwner={isOwner}
                    />
                </div>
            </div>

            {/* Modals */}
            {showAddAddressModal && (
                <AddAddressModal
                    customerId={id}
                    open
                    onClose={() => setShowAddAddressModal(false)}
                    onAdded={() => {
                        setShowAddAddressModal(false);
                        customerQuery.refetch();
                    }}
                />
            )}

            {deleteAddressTarget && (
                <DeleteAddressModal
                    customerId={id}
                    address={deleteAddressTarget}
                    open
                    onClose={() => setDeleteAddressTarget(null)}
                    onDeleted={() => {
                        setDeleteAddressTarget(null);
                        customerQuery.refetch();
                    }}
                />
            )}

            {showDisableModal && (
                <DisableModal
                    customerId={id}
                    customerName={customerDisplayName(customer)}
                    open
                    onClose={() => setShowDisableModal(false)}
                    onDisabled={() => {
                        setShowDisableModal(false);
                        customerQuery.refetch();
                        queryClient.invalidateQueries({ queryKey: ['customers'] });
                    }}
                />
            )}

            {showEnableModal && (
                <EnableModal
                    customerId={id}
                    customerName={customerDisplayName(customer)}
                    open
                    onClose={() => setShowEnableModal(false)}
                    onEnabled={() => {
                        setShowEnableModal(false);
                        customerQuery.refetch();
                        queryClient.invalidateQueries({ queryKey: ['customers'] });
                    }}
                />
            )}
        </div>
    );
}

// ============================================================================
// Contact card — email + phone, mirroring the RegisteredKeeperCard shape
// ============================================================================
function ContactCard({ customer, isOwner }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const contactPerson = customerContactPerson(customer);

    const portalStatus = customer.portal_status || 'not_invited';

    // Direct portal-invite mutation. Clerk handles the actual email flow
    // (webhook → Resend), so there's no synchronous body to preview here.
    const inviteMutation = useMutation({
        mutationFn: () => api.post(`/customers/${customer.id}/portal-invite`, {}),
        onSuccess: () => {
            toast.success(
                portalStatus === 'invited'
                    ? 'Invite resent'
                    : 'Portal invite sent'
            );
            queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
        },
        onError: (err) => {
            toast.error(err.message || 'Could not send invite');
        },
    });

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Contact</h2>
            </div>
            <div className="card-body">
                <dl className="space-y-3">
                    {customer.company_name && (
                        <>
                            <div>
                                <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Company</dt>
                                <dd className="text-sm text-ink mt-0.5 flex items-center gap-1.5">
                                    <Building2 className="h-3.5 w-3.5 text-ink-subtle flex-shrink-0" />
                                    <span className="break-words">{customer.company_name}</span>
                                </dd>
                            </div>
                            {contactPerson && (
                                <div>
                                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Contact person</dt>
                                    <dd className="text-sm text-ink mt-0.5">{contactPerson}</dd>
                                </div>
                            )}
                        </>
                    )}
                    <div>
                        <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Email</dt>
                        <dd className="text-sm text-ink mt-0.5 flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 text-ink-subtle flex-shrink-0" />
                            <a href={`mailto:${customer.email}`} className="hover:text-ink-muted truncate">
                                {customer.email}
                            </a>
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Phone</dt>
                        <dd className="text-sm text-ink mt-0.5 flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5 text-ink-subtle flex-shrink-0" />
                            <a href={`tel:${customer.phone}`} className="hover:text-ink-muted">
                                {customer.phone}
                            </a>
                        </dd>
                    </div>
                    {customer.address && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Address</dt>
                            <dd className="text-sm text-ink mt-0.5 flex items-start gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-ink-subtle flex-shrink-0 mt-0.5" />
                                <span className="break-words">{customer.address}</span>
                            </dd>
                        </div>
                    )}
                    {customer.billing_address_differs && customer.billing_address && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Billing address</dt>
                            <dd className="text-sm text-ink mt-0.5 flex items-start gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-ink-subtle flex-shrink-0 mt-0.5" />
                                <span className="break-words">{customer.billing_address}</span>
                            </dd>
                        </div>
                    )}
                </dl>
                {isOwner && customer.status === 'active' && (
                    <div className="mt-4 pt-4 border-t border-paper-border-soft">
                        <div className="text-xs text-ink-subtle uppercase tracking-wider font-medium mb-2">
                            Portal access
                        </div>
                        {portalStatus === 'active' && (
                            <div>
                                <StatusPill status="success">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Active in portal
                                </StatusPill>
                                <p className="text-xs text-ink-subtle mt-2">
                                    First signed in {formatDate(customer.portal_first_signed_in_at)}
                                </p>
                            </div>
                        )}

                        {portalStatus === 'invited' && (
                            <div>
                                <StatusPill status="warning">
                                    <Clock className="h-3 w-3" />
                                    Awaiting first sign-in
                                </StatusPill>
                                <p className="text-xs text-ink-subtle mt-2">
                                    Invite sent {formatDate(customer.portal_invited_at)}.
                                </p>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => inviteMutation.mutate()}
                                    disabled={!customer.email || inviteMutation.isPending}
                                    loading={inviteMutation.isPending}
                                    className="mt-3 w-full"
                                >
                                    <Mail className="h-3.5 w-3.5" />
                                    Resend invite
                                </Button>
                            </div>
                        )}

                        {portalStatus === 'not_invited' && (
                            <div>
                                <p className="text-sm text-ink-subtle">
                                    This customer hasn't been invited to the portal yet.
                                </p>
                                {!customer.email && (
                                    <p className="text-xs text-status-warning mt-2">
                                        Add an email address before sending an invite.
                                    </p>
                                )}
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => inviteMutation.mutate()}
                                    disabled={!customer.email || inviteMutation.isPending}
                                    loading={inviteMutation.isPending}
                                    className="mt-3 w-full"
                                >
                                    <Mail className="h-3.5 w-3.5" />
                                    Send portal invite
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function NotificationsCard({ customer, onCustomerChanged }) {
    const api = useApi();
    const [enabled, setEnabled] = useState(customer.auto_status_emails_enabled !== false);
    const [smsEnabled, setSmsEnabled] = useState(customer.auto_status_sms_enabled === true);
        const [officeEnabled, setOfficeEnabled] = useState(customer.office_status_updates_enabled === true);
    const [priceOnConfirmation, setPriceOnConfirmation] = useState(customer.show_price_on_confirmation === true);
    const [hideAccountName, setHideAccountName] = useState(customer.hide_account_name_from_chauffeur === true);
    const [showLicences, setShowLicences] = useState(customer.show_licence_numbers_in_emails === true);
    const [saving, setSaving] = useState(false);
    async function handleToggle(checked) {
        setSaving(true);
        setEnabled(checked); // optimistic
        try {
            await api.patch(`/customers/${customer.id}`, {
                auto_status_emails_enabled: checked,
            });
            toast.success(checked ? 'Auto status emails enabled' : 'Auto status emails disabled');
            onCustomerChanged?.();
        } catch (err) {
            setEnabled(!checked); // revert
            toast.error(err.message || 'Could not update');
        } finally {
            setSaving(false);
        }
    }

    async function handleSmsToggle(checked) {
        setSaving(true);
        setSmsEnabled(checked); // optimistic
        try {
            await api.patch(`/customers/${customer.id}`, {
                auto_status_sms_enabled: checked,
            });
            toast.success(checked ? 'Auto status SMS enabled' : 'Auto status SMS disabled');
            onCustomerChanged?.();
        } catch (err) {
            setSmsEnabled(!checked); // revert
            toast.error(err.message || 'Could not update');
        } finally {
            setSaving(false);
        }
    }

    async function handleOfficeToggle(checked) {
        setSaving(true);
        setOfficeEnabled(checked); // optimistic
        try {
            await api.patch(`/customers/${customer.id}`, {
                office_status_updates_enabled: checked,
            });
            toast.success(checked ? 'Office status updates enabled' : 'Office status updates disabled');
            onCustomerChanged?.();
        } catch (err) {
            setOfficeEnabled(!checked); // revert
            toast.error(err.message || 'Could not update');
        } finally {
            setSaving(false);
        }
    }

    async function handlePriceToggle(checked) {
        setSaving(true);
        setPriceOnConfirmation(checked); // optimistic
        try {
            await api.patch(`/customers/${customer.id}`, {
                show_price_on_confirmation: checked,
            });
            toast.success(checked ? 'Price shown on confirmation' : 'Price hidden on confirmation');
            onCustomerChanged?.();
        } catch (err) {
            setPriceOnConfirmation(!checked); // revert
            toast.error(err.message || 'Could not update');
        } finally {
            setSaving(false);
        }
    }

        async function handleShowLicencesToggle(checked) {
        setSaving(true);
        setShowLicences(checked); // optimistic
        try {
            await api.patch(`/customers/${customer.id}`, {
                show_licence_numbers_in_emails: checked,
            });
            toast.success(checked ? 'Licence numbers shown in emails' : 'Licence numbers hidden in emails');
            onCustomerChanged?.();
        } catch (err) {
            setShowLicences(!checked); // revert
            toast.error(err.message || 'Could not update');
        } finally {
            setSaving(false);
        }
    }

    async function handleHideAccountNameToggle(checked) {
        setSaving(true);
        setHideAccountName(checked); // optimistic
        try {
            await api.patch(`/customers/${customer.id}`, {
                hide_account_name_from_chauffeur: checked,
            });
            toast.success(checked ? 'Account name hidden from chauffeurs' : 'Account name shown to chauffeurs');
            onCustomerChanged?.();
        } catch (err) {
            setHideAccountName(!checked); // revert
            toast.error(err.message || 'Could not update');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Notifications</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Controls automated emails sent to this customer's bookers.
                </p>
            </div>
            <div className="card-body space-y-4">
                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => handleToggle(e.target.checked)}
                        disabled={saving}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Send automated status emails</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            Auto-fires when chauffeur taps en route, arrived, on journey, or completed.
                            Manual emails are unaffected. Turn off for channel-partner customers who manage
                            status updates in their own system.
                        </div>
                    </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={smsEnabled}
                        onChange={(e) => handleSmsToggle(e.target.checked)}
                        disabled={saving}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Send automated status SMS</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            Sends a short text to the booker (and passenger if notifications are on)
                            when the chauffeur taps en route, arrived, on journey, or completed.
                            Independent of email — can be used alongside or instead.
                        </div>
                    </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={officeEnabled}
                        onChange={(e) => handleOfficeToggle(e.target.checked)}
                        disabled={saving}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Office receives status updates</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            When on, info@lanzcts.com gets a plain-text heads-up at the same time
                            (en route, arrived, POB, completed, no show). Useful for jobs where
                             no dispatcher is at the screen. Independent of the customer's setting above.
                        </div>
                    </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={priceOnConfirmation}
                        onChange={(e) => handlePriceToggle(e.target.checked)}
                        disabled={saving}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Show price on confirmation email</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            Adds the total to this customer's booking confirmation email. Off by
                            default. Updates, cancellations, completed and chauffeur-status emails
                            never show price.
                        </div>
                    </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={hideAccountName}
                        onChange={(e) => handleHideAccountNameToggle(e.target.checked)}
                        disabled={saving}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Hide account name from chauffeurs</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            Removes the Account (company) line from the chauffeur allocation email,
                            consolidated chauffeur email, and job sheet. Off by default. Only affects
                            company customers — individuals have no account line to hide.
                        </div>
                    </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={showLicences}
                        onChange={(e) => handleShowLicencesToggle(e.target.checked)}
                        disabled={saving}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Show licence numbers in chauffeur emails</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            Adds the chauffeur's PHD licence and the vehicle's PHV licence to the
                            chauffeur details in customer emails. Off by default — turn on for clients
                            who need them for vetting (e.g. corporate security, embassies).
                        </div>
                    </div>
                </label>
            </div>
        </div>
    );
}

// ============================================================================
// Preferences card — payment terms + default pickup notes
// (Account billing toggle moved to PaymentMethodsTab — sits beneath the
// cards list since cards are the preferred payment method.)
// ============================================================================
function PreferencesCard({ customer, discount }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Preferences</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Default settings for this customer. Pickup notes are not auto-applied.
                </p>
            </div>
            <div className="card-body">
                <dl className="space-y-3">
                    {discount && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Discount</dt>
                            <dd className="text-sm text-ink mt-0.5">
                                <span className="font-semibold">{Number(discount.discount_pct)}%</span>
                                <span className="text-ink-subtle"> off fare</span>
                                {' \u00b7 '}
                                <a href="/pricing" className="text-xs text-ink-subtle hover:text-ink underline">
                                    Manage in Pricing
                                </a>
                            </dd>
                        </div>
                    )}
                    {customer.payment_terms && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Payment terms</dt>
                            <dd className="text-sm text-ink mt-0.5">{customer.payment_terms}</dd>
                        </div>
                    )}
                    {customer.default_pickup_notes && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Default pickup notes</dt>
                            <dd className="text-sm text-ink mt-0.5 whitespace-pre-wrap break-words">
                                {customer.default_pickup_notes}
                            </dd>
                        </div>
                    )}
                </dl>
            </div>
        </div>
    );
}

// ============================================================================
// Addresses card — list of saved addresses with per-address delete
// ============================================================================
function AddressesCard({ addresses, isAdmin, onAdd, onDelete }) {
    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-ink">Saved addresses</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Frequent pickup and dropoff points — will pre-fill on new bookings.
                    </p>
                </div>
                {isAdmin && (
                    <Button size="sm" variant="secondary" onClick={onAdd}>
                        <Plus className="h-3.5 w-3.5" />
                        Add
                    </Button>
                )}
            </div>
            {addresses.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No saved addresses.</p>
                </div>
            ) : (
                <ul className="divide-y divide-paper-border-soft">
                    {addresses.map((addr) => (
                        <AddressRow
                            key={addr.id}
                            address={addr}
                            isAdmin={isAdmin}
                            onDelete={() => onDelete(addr)}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}

function AddressRow({ address, isAdmin, onDelete }) {
    const iconType = classifyAddressIcon(address.address);
    const Icon = iconType === 'airport' ? Plane : iconType === 'station' ? Train : MapPin;

    return (
        <li className="px-6 py-4 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
                <Icon className="h-4 w-4 text-ink-subtle flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">{address.label}</div>
                    <div className="text-xs text-ink-muted mt-0.5 break-words">{address.address}</div>
                    {address.notes && (
                        <div className="text-xs text-ink-subtle mt-1 italic break-words">{address.notes}</div>
                    )}
                </div>
            </div>
            {isAdmin && (
                <button
                    type="button"
                    onClick={onDelete}
                    className="text-ink-muted hover:text-status-danger transition-colors flex-shrink-0 mt-0.5"
                    aria-label="Delete address"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            )}
        </li>
    );
}

// ============================================================================
// Customer sub-resources — Bookings / Bookers / Passengers / Payment methods tabs
// ============================================================================
function CustomerSubresources({ customerId, customer, isOwner, onCustomerChanged }) {
    const [activeTab, setActiveTab] = useState('bookings');

   const tabs = [
    { id: 'bookings',        label: 'Bookings',        icon: Receipt },
    { id: 'bookers',         label: 'Bookers',         icon: Users },
    { id: 'passengers',      label: 'Passengers',      icon: UserRound },
    { id: 'payment_methods', label: 'Payment methods', icon: CreditCard },
];

    return (
        <div className="card">
            {/* Tab strip — lives inside the card's header region so the visual
                weight matches the other cards on the right column. */}
            <div className="border-b border-paper-border-soft relative">
    <nav
        className="flex gap-1 px-4 pt-2 overflow-x-auto [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        aria-label="Tabs"
    >
        {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
                <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors whitespace-nowrap flex-shrink-0',
                        isActive
                            ? 'border-ink text-ink'
                            : 'border-transparent text-ink-muted hover:text-ink hover:bg-paper-hover/40'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                >
                    <Icon className="h-3.5 w-3.5" />
                    {tab.label}
                </button>
            );
        })}
    </nav>
    {/* Soft fade + chevron hint — for non-technical customers (this UI is also
    used by my.lanzcts.com), the fade alone reads as "more here →". A
    subtle darker gradient sits on top of any background so it's visible
    regardless of card colour. The chevron makes the affordance unmistakable. */}
<div
    className="lg:hidden pointer-events-none absolute right-0 top-0 bottom-0 flex items-center pr-1.5"
    aria-hidden="true"
>
    <div className="absolute inset-0 w-16 right-0 left-auto bg-gradient-to-l from-black/20 to-transparent" />
    <ChevronRight className="relative h-4 w-4 text-ink-muted" />
</div>
</div>

            {activeTab === 'bookings'   && <BookingsTab   customerId={customerId} />}
            {activeTab === 'bookers'    && <BookersTab    customerId={customerId} />}
            {activeTab === 'passengers' && <PassengersTab customerId={customerId} />}
            {activeTab === 'payment_methods' && (
                <PaymentMethodsTab
                    customerId={customerId}
                    customer={customer}
                    isOwner={isOwner}
                    onCustomerChanged={onCustomerChanged}
                />
            )}
        </div>
    );
}

// Bookings tab on customer detail page. Cap the inline list at 5 most recent
// to keep the page scannable — full history is a click away via the
// "View all in Bookings" link, which navigates to /bookings filtered by
// customer_id (handled in BookingsListPage's URL param hydration).
const BOOKINGS_TAB_VISIBLE_LIMIT = 5;
const SUBRESOURCE_TAB_VISIBLE_LIMIT = 5;

function BookingsTab({ customerId }) {
    const api = useApi();
    const navigate = useNavigate();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['customer', customerId, 'bookings'],
        queryFn: () => api.get(`/customers/${customerId}/bookings`),
        enabled: Boolean(customerId),
    });

    if (isLoading) return <EmptyState text="Loading bookings…" />;
    if (isError)   return <EmptyState text="Could not load bookings." />;
    const bookings = data?.bookings || [];
    if (bookings.length === 0) {
        return <EmptyState text="No bookings yet. Rides will appear here once this customer makes their first booking." />;
    }

    const visible = bookings.slice(0, BOOKINGS_TAB_VISIBLE_LIMIT);
    const hiddenCount = bookings.length - visible.length;
    // URL-encode the customer_id for safety (UUIDs don't strictly need it, but
    // good hygiene in case the param ever becomes anything else).
    const viewAllUrl = `/bookings?customer_id=${encodeURIComponent(customerId)}&date_preset=&status=`;

    return (
        <>
            <div className="divide-y divide-paper-border-soft">
                {visible.map((b) => (
                    <button
                        key={b.id}
                        type="button"
                        onClick={() => navigate(`/bookings/${b.id}`)}
                        className="w-full text-left px-6 py-4 hover:bg-paper-hover/40 transition-colors"
                    >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-xs bg-ink text-paper px-1.5 py-0.5 rounded">
                                        {b.ride_ref}
                                    </span>
                                    <BookingStatusPill status={b.status} />
                                    {b.source && b.source !== 'engine' && (
                                        <StatusPill status="neutral">{b.source}</StatusPill>
                                    )}
                                </div>
                                <div className="mt-1.5 text-sm text-ink break-words">
                                    <span className="font-medium">{b.pickup_address}</span>
                                    <span className="text-ink-subtle"> → </span>
                                    <span className="font-medium">{b.dropoff_address}</span>
                                </div>
                                <div className="mt-1 text-xs text-ink-subtle">
                                    {formatDate(b.pickup_date)} at {b.pickup_time}
                                    {b.mode === 'hourly' && b.final_time && ` until ${b.final_time}`}
                                    {' · '}
                                    {b.vehicle_class_label || b.vehicle_class}
                                    {b.passenger_name && ` · ${b.passenger_name}`}
                                    {!b.passenger_name && b.pax_name && ` · ${b.pax_name}`}
                                </div>
                            </div>
                            {b.charged_amount != null && (
                                <div className="text-right text-sm font-medium text-ink whitespace-nowrap">
                                    £{Number(b.charged_amount).toFixed(2)}
                                </div>
                            )}
                        </div>
                    </button>
                ))}
            </div>

            {hiddenCount > 0 && (
                <div className="px-6 py-3 border-t border-paper-border-soft flex items-center justify-between">
                    <span className="text-xs text-ink-subtle">
                        Showing 5 of {bookings.length}
                    </span>
                    <button
                        type="button"
                        onClick={() => navigate(viewAllUrl)}
                        className="text-xs font-medium text-ink hover:underline inline-flex items-center gap-1"
                    >
                        View all in Bookings
                        <ChevronRight className="h-3 w-3" />
                    </button>
                </div>
            )}
        </>
    );
}

function BookersTab({ customerId }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [showAddModal, setShowAddModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const customerQuery = useQuery({
        queryKey: ['customer', customerId],
        queryFn: () => api.get(`/customers/${customerId}`),
        enabled: Boolean(customerId),
    });

    const { data, isLoading, isError } = useQuery({
        queryKey: ['customer', customerId, 'bookers'],
        queryFn: () => api.get(`/customers/${customerId}/bookers`),
        enabled: Boolean(customerId),
    });

if (isLoading) return <EmptyState text="Loading bookers…" />;
    if (isError)   return <EmptyState text="Could not load bookers." />;
    const bookers = data?.bookers || [];
    const isFirst = bookers.length === 0;
    const visibleBookers = showAll ? bookers : bookers.slice(0, SUBRESOURCE_TAB_VISIBLE_LIMIT);
    const hiddenCount = bookers.length - visibleBookers.length;

    function handleChanged() {
        setShowAddModal(false);
        setEditTarget(null);
        setDeleteTarget(null);
        queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'bookers'] });
    }

    return (
        <>
            <div className="px-4 py-3 border-b border-paper-border-soft flex justify-end">
                <Button size="sm" variant="secondary" onClick={() => setShowAddModal(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    Add booker
                </Button>
            </div>

            {bookers.length === 0 ? (
                <EmptyState text="No bookers yet. People who book on behalf of this customer will appear here." />
            ) : (
             <ul className="divide-y divide-paper-border-soft">
                    {visibleBookers.map((b) => (
                        <li key={b.id} className="px-6 py-4 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink">{b.name}</div>
                                <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted">
                                    <Mail className="h-3 w-3 flex-shrink-0" />
                                    <a href={`mailto:${b.email}`} className="hover:text-ink truncate">{b.email}</a>
                                </div>
                                {b.phone && (
                                    <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted">
                                        <Phone className="h-3 w-3 flex-shrink-0" />
                                        <a href={`tel:${b.phone}`} className="hover:text-ink">{b.phone}</a>
                                    </div>
                                )}
                                <div className="mt-1.5 text-xs text-ink-subtle">
                                    {b.booking_count || 0} booking{Number(b.booking_count) === 1 ? '' : 's'}
                                    {b.last_booking_date && ` · Last: ${formatDate(b.last_booking_date)}`}
                                    {b.source && b.source !== 'engine' && ' · '}
                                    {b.source && b.source !== 'engine' && (
                                        <StatusPill status="neutral">{b.source}</StatusPill>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setEditTarget(b)}
                                    className="p-1.5 text-ink-muted hover:text-ink hover:bg-paper-hover/50 rounded transition-colors"
                                    aria-label="Edit booker"
                                    title="Edit"
                                >
                                    <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDeleteTarget(b)}
                                    className="p-1.5 text-ink-muted hover:text-status-danger hover:bg-paper-hover/50 rounded transition-colors"
                                    aria-label="Delete booker"
                                    title="Delete"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                       </li>
                    ))}
                </ul>
            )}

            {bookers.length > SUBRESOURCE_TAB_VISIBLE_LIMIT && (
                <div className="px-6 py-3 border-t border-paper-border-soft flex items-center justify-between">
                    <span className="text-xs text-ink-subtle">
                        {showAll
                            ? `Showing all ${bookers.length}`
                            : `Showing ${visibleBookers.length} of ${bookers.length}`}
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowAll((s) => !s)}
                        className="text-xs font-medium text-ink hover:underline"
                    >
                        {showAll ? 'Show fewer' : `Show all (${bookers.length})`}
                    </button>
                </div>
            )}

            {showAddModal && (
                <AddBookerModal
                    customerId={customerId}
                    customer={customerQuery.data}
                    prefill={isFirst}
                    open
                    onClose={() => setShowAddModal(false)}
                    onAdded={handleChanged}
                />
            )}

            {editTarget && (
                <EditBookerModal
                    customerId={customerId}
                    booker={editTarget}
                    open
                    onClose={() => setEditTarget(null)}
                    onSaved={handleChanged}
                />
            )}

            {deleteTarget && (
                <DeleteBookerModal
                    customerId={customerId}
                    booker={deleteTarget}
                    open
                    onClose={() => setDeleteTarget(null)}
                    onDeleted={handleChanged}
                />
            )}
        </>
    );
}
function PassengersTab({ customerId }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [showAddModal, setShowAddModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const customerQuery = useQuery({
        queryKey: ['customer', customerId],
        queryFn: () => api.get(`/customers/${customerId}`),
        enabled: Boolean(customerId),
    });

    const { data, isLoading, isError } = useQuery({
        queryKey: ['customer', customerId, 'passengers'],
        queryFn: () => api.get(`/customers/${customerId}/passengers`),
        enabled: Boolean(customerId),
    });

 if (isLoading) return <EmptyState text="Loading passengers…" />;
    if (isError)   return <EmptyState text="Could not load passengers." />;
    const passengers = data?.passengers || [];
    const isFirst = passengers.length === 0;
    const visiblePassengers = showAll ? passengers : passengers.slice(0, SUBRESOURCE_TAB_VISIBLE_LIMIT);
    const hiddenCount = passengers.length - visiblePassengers.length;

    function handleChanged() {
        setShowAddModal(false);
        setEditTarget(null);
        setDeleteTarget(null);
        queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'passengers'] });
    }

    return (
        <>
            <div className="px-4 py-3 border-b border-paper-border-soft flex justify-end">
                <Button size="sm" variant="secondary" onClick={() => setShowAddModal(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    Add passenger
                </Button>
            </div>

            {passengers.length === 0 ? (
                <EmptyState text="No passengers yet. People who travel under this customer will appear here." />
            ) : (
 <ul className="divide-y divide-paper-border-soft">
                    {visiblePassengers.map((p) => (
                        <li key={p.id} className="px-6 py-4 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink">{p.name}</div>
                                {p.phone && (
                                    <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted">
                                        <Phone className="h-3 w-3 flex-shrink-0" />
                                        <a href={`tel:${p.phone}`} className="hover:text-ink">{p.phone}</a>
                                    </div>
                                )}
                                {p.email && (
                                    <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted">
                                        <Mail className="h-3 w-3 flex-shrink-0" />
                                        <a href={`mailto:${p.email}`} className="hover:text-ink truncate">{p.email}</a>
                                    </div>
                                )}
                                <div className="mt-1.5">
                                    {p.notifications_enabled ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-status-success">
                                            <Bell className="h-3 w-3" />
                                            Notifications on
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-xs text-ink-subtle">
                                            <BellOff className="h-3 w-3" />
                                            Notifications off
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 text-xs text-ink-subtle">
                                    {p.booking_count || 0} ride{Number(p.booking_count) === 1 ? '' : 's'}
                                    {p.last_booking_date && ` · Last: ${formatDate(p.last_booking_date)}`}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setEditTarget(p)}
                                    className="p-1.5 text-ink-muted hover:text-ink hover:bg-paper-hover/50 rounded transition-colors"
                                    aria-label="Edit passenger"
                                    title="Edit"
                                >
                                    <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDeleteTarget(p)}
                                    className="p-1.5 text-ink-muted hover:text-status-danger hover:bg-paper-hover/50 rounded transition-colors"
                                    aria-label="Delete passenger"
                                    title="Delete"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {passengers.length > SUBRESOURCE_TAB_VISIBLE_LIMIT && (
                <div className="px-6 py-3 border-t border-paper-border-soft flex items-center justify-between">
                    <span className="text-xs text-ink-subtle">
                        {showAll
                            ? `Showing all ${passengers.length}`
                            : `Showing ${visiblePassengers.length} of ${passengers.length}`}
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowAll((s) => !s)}
                        className="text-xs font-medium text-ink hover:underline"
                    >
                        {showAll ? 'Show fewer' : `Show all (${passengers.length})`}
                    </button>
                </div>
            )}

            {showAddModal && (
                <AddPassengerModal
                    customerId={customerId}
                    customer={customerQuery.data}
                    prefill={isFirst}
                    open
                    onClose={() => setShowAddModal(false)}
                    onAdded={handleChanged}
                />
            )}

            {editTarget && (
                <EditPassengerModal
                    customerId={customerId}
                    passenger={editTarget}
                    open
                    onClose={() => setEditTarget(null)}
                    onSaved={handleChanged}
                />
            )}

            {deleteTarget && (
                <DeletePassengerModal
                    customerId={customerId}
                    passenger={deleteTarget}
                    open
                    onClose={() => setDeleteTarget(null)}
                    onDeleted={handleChanged}
                />
            )}
        </>
    );
}

function EmptyState({ text }) {
    return <div className="px-6 py-10 text-center text-sm text-ink-subtle">{text}</div>;
}

function BookingStatusPill({ status }) {
    const map = {
        enquiry:   { variant: 'neutral', label: 'Enquiry' },
        confirmed: { variant: 'success', label: 'Confirmed' },
        assigned:  { variant: 'success', label: 'Assigned' },
        on_route:  { variant: 'success', label: 'On route' },
        arrived:   { variant: 'success', label: 'Arrived' },
        completed: { variant: 'neutral', label: 'Completed' },
        cancelled: { variant: 'danger',  label: 'Cancelled' },
    };
    const cfg = map[status] || { variant: 'neutral', label: status };
    return <StatusPill status={cfg.variant}>{cfg.label}</StatusPill>;
}

// ============================================================================
// Notes card — composer + list, mirrors vehicle notes exactly
// ============================================================================
function NotesCard({ customerId, notes, isOwner }) {
    const [composerText, setComposerText] = useState('');
    const api = useApi();
    const queryClient = useQueryClient();

    const addNote = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/notes`, { note_text: composerText.trim() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
            setComposerText('');
            toast.success('Note added');
        },
        onError: (err) => toast.error(err.message || 'Could not add note'),
    });

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Notes</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Timestamped notes — preferences, incidents, reminders, anything worth remembering.
                </p>
            </div>

            <div className="card-body border-b border-paper-border-soft">
                <Textarea
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    placeholder="Add a note…"
                    rows={2}
                    maxLength={5000}
                />
                <div className="mt-3 flex justify-end">
                    <Button
                        size="sm"
                        onClick={() => addNote.mutate()}
                        disabled={!composerText.trim() || addNote.isPending}
                        loading={addNote.isPending}
                    >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Add note
                    </Button>
                </div>
            </div>

            {notes.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No notes yet.</p>
                </div>
            ) : (
                <ul className="divide-y divide-paper-border-soft">
                    {notes.map((note) => (
                        <li key={note.id} className="px-6 py-4">
                            <p className="text-sm text-ink whitespace-pre-wrap break-words">{note.note_text}</p>
                            <p className="mt-1.5 text-xs text-ink-subtle">
                                {note.author_email || 'Unknown'} · {formatDateTime(note.created_at)}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
// ============================================================================
// Saved chauffeur notes card — reusable note snippets, mirrors AddressesCard
// ============================================================================
// Distinct from the diary NotesCard above: these are short, reusable
// instructions ("Prefers still water", "Use side entrance") that pre-fill on
// new bookings — picked and appended to a ride's "Notes for chauffeur" field.
function SavedNotesCard({ customerId, savedNotes, isAdmin, onChanged }) {
    const api = useApi();
    const [showAdd, setShowAdd] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const deleteMutation = useMutation({
        mutationFn: (noteId) => api.delete(`/customers/${customerId}/saved-notes/${noteId}`),
        onSuccess: () => {
            toast.success('Saved note removed');
            setDeleteTarget(null);
            onChanged?.();
        },
        onError: (err) => toast.error(err.message || 'Could not remove note'),
    });

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-ink">Saved chauffeur notes</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Reusable instructions — pick and add them to the chauffeur notes on new bookings.
                    </p>
                </div>
                {isAdmin && (
                    <Button size="sm" variant="secondary" onClick={() => setShowAdd(true)}>
                        <Plus className="h-3.5 w-3.5" />
                        Add
                    </Button>
                )}
            </div>
            {savedNotes.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No saved notes.</p>
                </div>
            ) : (
                <ul className="divide-y divide-paper-border-soft">
                    {savedNotes.map((n) => (
                        <li key={n.id} className="px-6 py-4 flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                                {n.label && (
                                    <div className="text-sm font-medium text-ink">{n.label}</div>
                                )}
                                <div className={`text-sm break-words ${n.label ? 'text-ink-muted mt-0.5' : 'text-ink'}`}>
                                    {n.note_text}
                                </div>
                            </div>
                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={() => setDeleteTarget(n)}
                                    className="text-ink-muted hover:text-status-danger transition-colors flex-shrink-0 mt-0.5"
                                    aria-label="Delete saved note"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {showAdd && (
                <AddSavedNoteModal
                    customerId={customerId}
                    open
                    onClose={() => setShowAdd(false)}
                    onAdded={() => { setShowAdd(false); onChanged?.(); }}
                />
            )}

            {deleteTarget && (
                <Modal
                    open
                    onClose={() => setDeleteTarget(null)}
                    title="Delete saved note?"
                    description="This removes the reusable note from this customer. Bookings that already used it keep their text."
                    size="md"
                >
                    <div className="bg-paper-hover/50 rounded-md p-3 text-sm text-ink-muted break-words">
                        {deleteTarget.label ? `${deleteTarget.label} — ` : ''}{deleteTarget.note_text}
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                        <Button
                            variant="danger"
                            onClick={() => deleteMutation.mutate(deleteTarget.id)}
                            loading={deleteMutation.isPending}
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function AddSavedNoteModal({ customerId, open, onClose, onAdded }) {
    const api = useApi();
    const [label, setLabel] = useState('');
    const [noteText, setNoteText] = useState('');
    const [noteError, setNoteError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/saved-notes`, {
            label: label.trim() || null,
            note_text: noteText.trim(),
        }),
        onSuccess: () => {
            toast.success('Saved note added');
            onAdded();
        },
        onError: (err) => {
            if (err.fieldErrors?.note_text) setNoteError(err.fieldErrors.note_text[0]);
            else toast.error(err.message || 'Could not add note');
        },
    });

    function handleSave() {
        setNoteError(null);
        if (!noteText.trim()) {
            setNoteError('Note text is required');
            return;
        }
        mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Add saved note"
            description="A reusable instruction for the chauffeur. Pick it when booking to add it to the ride's chauffeur notes."
            size="md"
        >
            <div className="space-y-4">
                <Input
                    label="Label (optional)"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder='e.g. "Water preference", "Access"'
                    hint="A short name to recognise it by. If blank, the note text shows instead."
                />
                <div>
                    <label className="block text-xs text-ink-muted uppercase tracking-wider font-medium mb-1.5">
                        Note <span className="text-status-danger">*</span>
                    </label>
                    <Textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder='e.g. "Prefers still water, room temperature"'
                        rows={3}
                        maxLength={1000}
                    />
                    {noteError && <div className="text-xs text-status-danger mt-1">{noteError}</div>}
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>
                    <Plus className="h-4 w-4" />
                    Save note
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Add booker modal — pre-fills from customer on first booker
// ============================================================================
function AddBookerModal({ customerId, customer, prefill, open, onClose, onAdded }) {
    const api = useApi();

    // Compose the contact person name from customer record. For company
    // customers, first/last_name is the contact person; for individuals,
    // it's the customer themselves. Either way, that's who becomes the
    // first booker by default.
    const defaultName = prefill && customer
        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
        : '';
    const defaultEmail = prefill && customer ? (customer.email || '') : '';
    const defaultPhone = prefill && customer ? (customer.phone || '') : '';

    const [name, setName]   = useState(defaultName);
    const [email, setEmail] = useState(defaultEmail);
    const [phone, setPhone] = useState(defaultPhone);
    const [nameError, setNameError]   = useState(null);
    const [emailError, setEmailError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/bookers`, {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
        }),
        onSuccess: () => {
            toast.success('Booker added');
            onAdded();
        },
        onError: (err) => {
            if (err.fieldErrors) {
                if (err.fieldErrors.name)  setNameError(err.fieldErrors.name[0]);
                if (err.fieldErrors.email) setEmailError(err.fieldErrors.email[0]);
                toast.error('Please fix the errors below');
            } else {
                toast.error(err.message || 'Could not add booker');
            }
        },
    });

    function handleSave() {
        setNameError(null);
        setEmailError(null);
        let ok = true;
        if (!name.trim()) {
            setNameError('Name is required');
            ok = false;
        }
        if (!email.trim()) {
            setEmailError('Email is required');
            ok = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setEmailError('Enter a valid email');
            ok = false;
        }
        if (ok) mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Add booker"
            description={prefill
                ? "Pre-filled from the customer's contact details. Edit if needed."
                : "Someone else who books on behalf of this customer."}
            size="md"
        >
            <div className="space-y-4">
                <Input
                    label="Name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    error={nameError}
                />
                <Input
                    label="Email"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    error={emailError}
                />
                <Input
                    label="Phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07000 000000"
                />
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>
                    <Plus className="h-4 w-4" />
                    Add booker
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Add passenger modal — pre-fills from customer on first passenger
// ============================================================================
function AddPassengerModal({ customerId, customer, prefill, open, onClose, onAdded }) {
    const api = useApi();

    const defaultName  = prefill && customer
        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
        : '';
    const defaultPhone = prefill && customer ? (customer.phone || '') : '';
    const defaultEmail = prefill && customer ? (customer.email || '') : '';

    const [name, setName]   = useState(defaultName);
    const [phone, setPhone] = useState(defaultPhone);
    const [email, setEmail] = useState(defaultEmail);
    const [nameError, setNameError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/passengers`, {
            name: name.trim(),
            phone: phone.trim() || null,
            email: email.trim() || null,
        }),
        onSuccess: () => {
            toast.success('Passenger added');
            onAdded();
        },
        onError: (err) => {
            if (err.fieldErrors?.name) setNameError(err.fieldErrors.name[0]);
            else toast.error(err.message || 'Could not add passenger');
        },
    });

    function handleSave() {
        setNameError(null);
        if (!name.trim()) {
            setNameError('Name is required');
            return;
        }
        mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Add passenger"
            description={prefill
                ? "Pre-filled from the customer's contact details. Edit if needed."
                : "Someone who travels under this customer's account."}
            size="md"
        >
            <div className="space-y-4">
                <Input
                    label="Name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    error={nameError}
                />
                <Input
                    label="Phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07000 000000"
                />
                <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Optional"
                />
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>
                    <Plus className="h-4 w-4" />
                    Add passenger
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Edit booker modal
// ============================================================================
function EditBookerModal({ customerId, booker, open, onClose, onSaved }) {
    const api = useApi();
    const [name, setName]   = useState(booker.name || '');
    const [email, setEmail] = useState(booker.email || '');
    const [phone, setPhone] = useState(booker.phone || '');
    const [nameError, setNameError]   = useState(null);
    const [emailError, setEmailError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.patch(`/customers/${customerId}/bookers/${booker.id}`, {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
        }),
        onSuccess: () => {
            toast.success('Booker updated');
            onSaved();
        },
        onError: (err) => {
            if (err.fieldErrors) {
                if (err.fieldErrors.name)  setNameError(err.fieldErrors.name[0]);
                if (err.fieldErrors.email) setEmailError(err.fieldErrors.email[0]);
                toast.error('Please fix the errors below');
            } else {
                toast.error(err.message || 'Could not update booker');
            }
        },
    });

    function handleSave() {
        setNameError(null);
        setEmailError(null);
        let ok = true;
        if (!name.trim()) { setNameError('Name is required'); ok = false; }
        if (!email.trim()) { setEmailError('Email is required'); ok = false; }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setEmailError('Enter a valid email'); ok = false; }
        if (ok) mutation.mutate();
    }

    return (
        <Modal open={open} onClose={onClose} title="Edit booker" size="md">
            <div className="space-y-4">
                <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} error={nameError} />
                <Input label="Email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={emailError} />
                <Input label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07000 000000" />
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>Save changes</Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Delete booker modal — booking-aware
// ============================================================================
function DeleteBookerModal({ customerId, booker, open, onClose, onDeleted }) {
    const api = useApi();
    const hasBookings = (booker.booking_count || 0) > 0;

    const mutation = useMutation({
        mutationFn: () => api.delete(`/customers/${customerId}/bookers/${booker.id}`),
        onSuccess: () => {
            toast.success('Booker deleted');
            onDeleted();
        },
        onError: (err) => toast.error(err.message || 'Could not delete booker'),
    });

    if (hasBookings) {
        return (
            <Modal
                open={open}
                onClose={onClose}
                title="Cannot delete booker"
                description={`${booker.name} has ${booker.booking_count} booking${Number(booker.booking_count) === 1 ? '' : 's'} attached. Bookers with booking history can't be removed — edit their details instead.`}
                size="md"
            >
                <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Delete booker?"
            description={`${booker.name} will be permanently removed. This is audit-logged but cannot be undone.`}
            size="md"
        >
            <div className="bg-paper-hover/50 rounded-md p-3 text-sm text-ink-muted break-words">
                {booker.email}{booker.phone ? ` · ${booker.phone}` : ''}
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                    <Trash2 className="h-4 w-4" />
                    Delete
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Edit passenger modal
// ============================================================================
function EditPassengerModal({ customerId, passenger, open, onClose, onSaved }) {
    const api = useApi();
    const [name, setName]   = useState(passenger.name || '');
    const [phone, setPhone] = useState(passenger.phone || '');
    const [email, setEmail] = useState(passenger.email || '');
    const [notificationsEnabled, setNotificationsEnabled] = useState(passenger.notifications_enabled || false);
    const [nameError, setNameError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.patch(`/customers/${customerId}/passengers/${passenger.id}`, {
            name: name.trim(),
            phone: phone.trim() || null,
            email: email.trim() || null,
            notifications_enabled: notificationsEnabled,
        }),
        onSuccess: () => {
            toast.success('Passenger updated');
            onSaved();
        },
        onError: (err) => {
            if (err.fieldErrors?.name) setNameError(err.fieldErrors.name[0]);
            else toast.error(err.message || 'Could not update passenger');
        },
    });

    function handleSave() {
        setNameError(null);
        if (!name.trim()) { setNameError('Name is required'); return; }
        mutation.mutate();
    }

    return (
        <Modal open={open} onClose={onClose} title="Edit passenger" size="md">
            <div className="space-y-4">
                <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} error={nameError} />
                <Input label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07000 000000" />
                <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Optional" />
                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={notificationsEnabled}
                        onChange={(e) => setNotificationsEnabled(e.target.checked)}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Enable booking notifications</div>
                        <div className="text-xs text-ink-subtle">When on, this passenger receives booking confirmation and ride status emails alongside the booker.</div>
                    </div>
                </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>Save changes</Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Delete passenger modal — booking-aware
// ============================================================================
function DeletePassengerModal({ customerId, passenger, open, onClose, onDeleted }) {
    const api = useApi();
    const hasBookings = (passenger.booking_count || 0) > 0;

    const mutation = useMutation({
        mutationFn: () => api.delete(`/customers/${customerId}/passengers/${passenger.id}`),
        onSuccess: () => {
            toast.success('Passenger deleted');
            onDeleted();
        },
        onError: (err) => toast.error(err.message || 'Could not delete passenger'),
    });

    if (hasBookings) {
        return (
            <Modal
                open={open}
                onClose={onClose}
                title="Cannot delete passenger"
                description={`${passenger.name} has ${passenger.booking_count} ride${Number(passenger.booking_count) === 1 ? '' : 's'} attached. Passengers with booking history can't be removed — edit their details instead.`}
                size="md"
            >
                <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Delete passenger?"
            description={`${passenger.name} will be permanently removed. This is audit-logged but cannot be undone.`}
            size="md"
        >
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                    <Trash2 className="h-4 w-4" />
                    Delete
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Add address modal — uses AddressInput + label + notes
// ============================================================================
function AddAddressModal({ customerId, open, onClose, onAdded }) {
    const api = useApi();
    const [label, setLabel] = useState('');
    const [addressData, setAddressData] = useState(null);
    const [notes, setNotes] = useState('');
    const [labelError, setLabelError] = useState(null);
    const [addressError, setAddressError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/addresses`, {
            label: label.trim(),
            address: addressData.address,
            postcode: addressData.postcode,
            latitude: addressData.latitude,
            longitude: addressData.longitude,
            google_place_id: addressData.google_place_id,
            address_source: addressData.address_source,
            notes: notes.trim() || null,
        }),
        onSuccess: () => {
            toast.success('Address added');
            onAdded();
        },
        onError: (err) => {
            if (err.fieldErrors) {
                if (err.fieldErrors.label) setLabelError(err.fieldErrors.label[0]);
                if (err.fieldErrors.address) setAddressError(err.fieldErrors.address[0]);
                toast.error('Please fix the errors below');
            } else {
                toast.error(err.message || 'Could not add address');
            }
        },
    });

    function handleSave() {
        setLabelError(null);
        setAddressError(null);
        let ok = true;
        if (!label.trim()) {
            setLabelError('Label is required');
            ok = false;
        }
        if (!addressData?.address) {
            setAddressError('Address is required');
            ok = false;
        }
        if (ok) mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Add address"
            description="Save a frequent location for this customer."
            size="md"
        >
            <div className="space-y-4">
                <Input
                    label="Label"
                    required
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder='e.g. "Home", "Office", "Mum\u2019s place"'
                    error={labelError}
                />
                <div>
                    <label className="block text-xs text-ink-muted uppercase tracking-wider font-medium mb-1.5">
                        Address <span className="text-status-danger">*</span>
                    </label>
                    <AddressInput
                        value={addressData?.address || ''}
                        onChange={setAddressData}
                        placeholder="Search address…"
                        error={addressError}
                    />
                </div>
                <Input
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder='Optional — e.g. "Gate code 4421", "Use side entrance"'
                    hint="Shown to chauffeurs on the job sheet."
                />
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    loading={mutation.isPending}
                >
                    <Plus className="h-4 w-4" />
                    Save address
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Delete address confirmation modal
// ============================================================================
function DeleteAddressModal({ customerId, address, open, onClose, onDeleted }) {
    const api = useApi();

    const mutation = useMutation({
        mutationFn: () => api.delete(`/customers/${customerId}/addresses/${address.id}`),
        onSuccess: () => {
            toast.success('Address deleted');
            onDeleted();
        },
        onError: (err) => toast.error(err.message || 'Could not delete address'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Delete address?"
            description={`"${address.label}" will be removed from this customer. This is audit-logged but cannot be undone.`}
            size="md"
        >
            <div className="bg-paper-hover/50 rounded-md p-3 text-sm text-ink-muted break-words">
                {address.address}
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="danger"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <Trash2 className="h-4 w-4" />
                    Delete
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Disable modal
// ============================================================================
function DisableModal({ customerId, customerName, open, onClose, onDisabled }) {
    const api = useApi();
    const [reason, setReason] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/disable`, { reason }),
        onSuccess: () => {
            toast.success('Customer disabled');
            onDisabled();
        },
        onError: (err) => toast.error(err.message || 'Could not disable customer'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Disable customer?"
            description={`${customerName} will be hidden from the default list view.`}
            size="md"
        >
            <Textarea
                label="Reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. duplicate record, requested account closure, no longer trading"
                rows={3}
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="danger"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                    disabled={!reason.trim()}
                >
                    <Ban className="h-4 w-4" />
                    Disable
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Enable modal
// ============================================================================
function EnableModal({ customerId, customerName, open, onClose, onEnabled }) {
    const api = useApi();
    const [reason, setReason] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/enable`, reason.trim() ? { reason: reason.trim() } : {}),
        onSuccess: () => {
            toast.success('Customer re-enabled');
            onEnabled();
        },
        onError: (err) => toast.error(err.message || 'Could not re-enable customer'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Re-enable customer?"
            description={`${customerName} will reappear in the default list.`}
            size="md"
        >
            <Textarea
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. duplicate merged, account reactivated"
                rows={3}
                hint="Optional — helpful context for the audit log."
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <RotateCcw className="h-4 w-4" />
                    Re-enable
                </Button>
            </div>
        </Modal>
    );
}

// ============================================================================
// Helpers
// ============================================================================
function formatDate(d) {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
    if (!d) return null;
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}
